import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCol } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { Box } from '@/UI/Box/Box';
import { fetchHandleAvailability } from '@/api/workflowCommunityPublishing.api';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FieldLabelRow } from './PublishToCommunityModalDescription';

const HANDLE_FORMAT_REGEX = /^[0-9a-zA-Z_]{1,15}$/;

const AVAILABILITY_DEBOUNCE_MS = 300;

export type HandleStatus =
  | { kind: 'empty' }
  | { kind: 'invalid_format' }
  | { kind: 'checking' }
  | { kind: 'available'; handle: string }
  | { kind: 'taken'; handle: string }
  | { kind: 'error' };

interface CommunityHandleSectionProps {
  handle: string;
  onHandleChange: (next: string) => void;
  onStatusChange: (status: HandleStatus) => void;
  submitError?: string | null;
}

const CommunityHandleSection = ({
  handle,
  onHandleChange,
  onStatusChange,
  submitError,
}: CommunityHandleSectionProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const [debouncedHandle, setDebouncedHandle] = useState(handle);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedHandle(handle), AVAILABILITY_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [handle]);

  const formatValid = HANDLE_FORMAT_REGEX.test(debouncedHandle);

  const availabilityQuery = useQuery({
    queryKey: ['community-handle-availability', debouncedHandle],
    queryFn: () => fetchHandleAvailability(debouncedHandle),
    enabled: formatValid,
    staleTime: 0,
  });

  const status = useMemo<HandleStatus>(() => {
    if (handle.length === 0) return { kind: 'empty' };
    if (!HANDLE_FORMAT_REGEX.test(handle)) return { kind: 'invalid_format' };
    if (handle !== debouncedHandle || availabilityQuery.isFetching) return { kind: 'checking' };
    if (availabilityQuery.isError) return { kind: 'error' };
    if (availabilityQuery.data?.available) return { kind: 'available', handle };
    if (availabilityQuery.data && !availabilityQuery.data.available) return { kind: 'taken', handle };
    return { kind: 'checking' };
  }, [handle, debouncedHandle, availabilityQuery.isFetching, availabilityQuery.isError, availabilityQuery.data]);

  useEffect(() => {
    onStatusChange(status);
  }, [status, onStatusChange]);

  return (
    <FlexCol sx={{ gap: 1 }}>
      <FieldLabelRow
        label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_SECTION_LABEL)}
      />
      <TextSmRg color={token.textSecondary}>
        {t(
          I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP
            .HANDLE_SECTION_DESCRIPTION_PREFIX,
        )}
        <a
          href={EXTERNAL_LINKS.figmaCommunityGuidelines}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: token.text, textDecoration: 'underline' }}
        >
          {t(
            I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_SECTION_GUIDELINES_LINK,
          )}
        </a>
        {t(
          I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP
            .HANDLE_SECTION_DESCRIPTION_SUFFIX,
        )}
      </TextSmRg>
      <Input
        value={handle}
        onChange={(e) => onHandleChange(e.target.value)}
        startAdornment={
          <Box component="span" sx={{ display: 'inline-flex', px: isFplMigrationEnabled ? 1 : 0 }}>
            <TextSmRg color={token.textSecondary}>figma.com/@</TextSmRg>
          </Box>
        }
        size="large"
        fullWidth
      />
      <HandleStatusText status={status} />
      {submitError ? <TextSmRg color={token.textDanger}>{submitError}</TextSmRg> : null}
    </FlexCol>
  );
};

const HandleStatusText = ({ status }: { status: HandleStatus }) => {
  const { t } = useTranslation();
  switch (status.kind) {
    case 'empty':
    case 'checking':
      return null;
    case 'available':
      return (
        <TextSmRg color={color.Green}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_AVAILABLE, {
            handle: status.handle,
          })}
        </TextSmRg>
      );
    case 'taken':
      return (
        <TextSmRg color={token.textDanger}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_TAKEN, {
            handle: status.handle,
          })}
        </TextSmRg>
      );
    case 'invalid_format':
      return (
        <TextSmRg color={token.textDanger}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_INVALID_FORMAT)}
        </TextSmRg>
      );
    case 'error':
      return (
        <TextSmRg color={token.textDanger}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DETAILS_STEP.HANDLE_CHECK_FAILED)}
        </TextSmRg>
      );
  }
};

export default CommunityHandleSection;
